import React, { useEffect } from 'react'
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from '@/components/ui/table'
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles'
import { AutoCompletePortal } from '@/components/ui/autocomplete-portal'
import { Button } from '@/components/ui/button'
import { Plus, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, X, FileText } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { Option } from '@/components/ui/autocomplete'
import PurchaseRequestItemsModal from './purchase-request-items-modal'
import PaginationButton from '@/components/ui/pagination-button'
import { useDebounce } from '@uidotdev/usehooks'
import { useQuery } from '@connectrpc/connect-query'
import { searchInventory } from '@/gen/pharmacy-PharmacyService_connectquery'
import { toast } from '../ui/use-toast'
import { AddNonMedical } from '@/components/add-non-medical'
import { ListPurchaseRequestsResponse } from '@/gen/purchase_request/list_purchase_requests_pb'

type Row = {
  product: Option | undefined;
  bonus: string;
  quantity: string;
  intendedCost: string;
  productInput: string;
  notes?: string;
  purchaseRequestId?: string[]; // Added to track purchase request ID
};

type CreatePurchaseOrderComponentProps = {
  rows: Row[];
  bonusRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  handleProductChange: (index: number, value: Option) => void;
  handleRowChange: (index: number, field: string, value: string) => void;
  handleClearRow: (index: number) => void;
  rowsPerPage?: number;
  bordersTrue?: boolean;
  handleSubmit: () => void;
  loading: boolean;
  purchaseRequests: ListPurchaseRequestsResponse
  handleBulkAdd: (items: Array<{
      product: Option | undefined;
      bonus: string;
      quantity: string;
      intendedCost: string;
      productInput: string;
      notes?: string;
      purchaseRequestId?: string[];
  }>) => void
};

export const CreatePurchaseOrderComponent: React.FC<CreatePurchaseOrderComponentProps> = ({
  rows,
  bonusRefs,
  handleProductChange,
  handleRowChange,
  handleClearRow,
  rowsPerPage = 10,
  bordersTrue = false,
  handleSubmit,
  loading,
  purchaseRequests,
  handleBulkAdd
}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const totalPages = Math.ceil(rows.length / rowsPerPage);
  const paginatedRows = rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Pagination logic for ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className={`flex flex-col h-full ${bordersTrue ? 'border rounded-lg' : ''}`}>
      <div className="flex-1 min-h-0 overflow-auto flex flex-col">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="text-sm">
              <TableHead className={tableHeadClass}>SL NO.</TableHead>
              <TableHead className={tableHeadClass}>
                <div className="flex items-center justify-between gap-2">
                  Product Name
                  <div className='text-gray-700'>
                    <AddNonMedical />
                  </div>
                </div>
              </TableHead>
              <TableHead className={tableHeadClass}>Quantity</TableHead>
              <TableHead className={tableHeadClass}>Bonus</TableHead>
              <TableHead className={tableHeadClass}>Intended Cost</TableHead>
              <TableHead className={tableHeadClass}></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedRows.map((row, idx, allRows) => {
              const realIdx = (currentPage - 1) * rowsPerPage + idx;
              return (
                <IndividualRow
                  key={realIdx}
                  row={row}
                  realIdx={realIdx}
                  bonusRefs={bonusRefs}
                  handleRowChange={handleRowChange}
                  handleClearRow={handleClearRow}
                  totalLength={allRows.length}
                  handleProductChange={handleProductChange}
                />
              );
            })}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-2 text-sm border-b bg-white">
            <p>{rows.length} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </PaginationButton>
              {getPageNumbers().map((page, idx) => (
                page === '...'
                  ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                  : <PaginationButton
                      key={page}
                      active={currentPage === page}
                      onClick={() => setCurrentPage(page as number)}
                      disabled={currentPage === page}
                    >
                      <span className="text-sm">{page}</span>
                    </PaginationButton>
              ))}
              <PaginationButton
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <ChevronsRight className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        )}
      </div>
      <div className="w-full border-t px-4 py-3 bg-white flex items-center justify-between gap-2">
        <Button variant="outline" onClick={() => setModalOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          {/* If any row has Purchase Request ID, then show add more else show add items from Purchase Request */}
          {rows.some(row => row.purchaseRequestId) ? 'Add More Items' : 'Add Items from Purchase Request'}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline">Save Draft</Button>
          <Button variant="default" className='bg-blue-700' onClick={() => handleSubmit()} disabled={loading}>Create Purchase Order</Button>
        </div>
      </div>
      <PurchaseRequestItemsModal open={modalOpen} onOpenChange={setModalOpen} purchaseRequests={purchaseRequests} handleBulkAdd={handleBulkAdd} />
    </div>
  );
};

const IndividualRow = ({
  row,
  realIdx,
  bonusRefs,
  handleRowChange,
  handleClearRow,
  totalLength,
  handleProductChange
}: {
  row: Row;
  realIdx: number;
  bonusRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  handleRowChange: (index: number, field: string, value: string) => void;
  handleClearRow: (index: number) => void;
  totalLength: number;
  handleProductChange: (index: number, value: Option) => void;
}) => {

  const [selectedOption, setSelectedOption] = React.useState<Option | undefined>(row.product);

  const [searchInput, setSearchInput] = React.useState('');
  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [options, setOptions] = React.useState<Option[]>([]);

  const debouncedSearch = useDebounce(searchInput, 500);

  const {data, error, isLoading} = useQuery(searchInventory, {
    query: debouncedSearch,
    limit: 5,
    onlyAvailable: false
  });

  useEffect(() => {
    if (data) {
      const formattedOptions = data.items.map(item => ({
        value: item.id,
        label: item.tradeName + (item.scientificName ? ` - ${item.scientificName}` : '') + ` (${item.unit} ${item.unitType})`,
        type: item.type.toString()
      }));
      setOptions(formattedOptions);
    }
  }, [data])

  useEffect(() => {
    if (row.product) {
      setSelectedOption(row.product);
      setSearchInput(row.product.label || '');
      setNote(row.notes || '');
    } else {
      setSelectedOption(undefined);
      setSearchInput('');
      setNote('');
    }
  }, [row.product])

  if (error) {
    toast({
      title: 'Error',
      description: 'Failed to fetch products. Please try again later. ' + error.message,
      variant: 'destructive',
      duration: 5000,
    })
  }


  return (
    <TableRow className="font-medium" key={realIdx}>
      <TableCell className={tableCellClass}>{realIdx + 1}</TableCell>
      <TableCell className={tableCellClass}>
        <AutoCompletePortal
          options={options}
          value={selectedOption}
          onValueChange={(value) => {
            setSelectedOption(value);
            handleProductChange(realIdx, value);
          }}
          input={searchInput}
          onInputChanged={setSearchInput}
          emptyMessage="No products found."
          placeholder="Select product"
          isLoading={isLoading}
          disabled={false}
          className=""
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          ref={el => (bonusRefs.current[realIdx] = el)}
          type="number"
          className="border rounded px-2 py-1 h-7 w-20"
          value={row.quantity}
          onChange={e => handleRowChange(realIdx, 'quantity', e.target.value)}
          disabled={!row.product}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          className="border rounded px-2 py-1 h-7 w-20"
          value={row.bonus}
          onChange={e => handleRowChange(realIdx, 'bonus', e.target.value)}
          disabled={!row.product}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          className="border rounded px-2 py-1 h-7 w-24"
          value={row.intendedCost}
          onChange={e => handleRowChange(realIdx, 'intendedCost', e.target.value)}
          disabled={!row.product}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Button
          variant="outline"
          size="xs"
          className="mr-2"
          onClick={() => setShowNotesModal(true)}
        >
          {
            <FileText className="w-4 h-4" />
          }
        </Button>
        {showNotesModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-lg p-4 w-80">
              <h3 className="font-semibold mb-2">Item Notes</h3>
              <textarea
                className="w-full border rounded p-2 mb-3 text-sm"
                rows={3}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Enter notes for this item..."
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNotesModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    handleRowChange(realIdx, 'notes', note);
                    setShowNotesModal(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="xs"
          onClick={() => handleClearRow(realIdx)}
          className="text-gray-600 hover:text-red-500 hover:bg-red-100 transition-colors"
          aria-label="Delete row"
          disabled={
            // Disabled if it's the only row or if the row is empty
            (totalLength <= 1 && row.product === undefined) || (row.product === undefined && row.quantity === '' && row.intendedCost === '' && row.bonus === '')
          }
        >
          <X className="w-4 h-4" />
        </Button>
      </TableCell>
    </TableRow>
  )
}

export default CreatePurchaseOrderComponent